import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'

import type { WebChatChannelCreated, WebChatChannelSummary, WebChatSecretRotated } from './types'

function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

/** Lista os widgets de WebChat do tenant (sem o signing secret). */
export async function listWebChatChannels(
  tenantId: string,
): Promise<readonly WebChatChannelSummary[]> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/webchat/channels', {
    params: { path: { tenantId } },
  })
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}

/** Cria um widget e devolve a publishable key + o signing secret (mostrado uma única vez). */
export async function createWebChatChannel(
  tenantId: string,
  name: string,
  allowedOrigins: readonly string[],
): Promise<WebChatChannelCreated> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/webchat/channels', {
    params: { path: { tenantId } },
    body: { name, allowedOrigins: [...allowedOrigins] },
  })
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}

/** Substitui a allow-list de origens do widget. */
export async function updateWebChatOrigins(
  tenantId: string,
  webChatChannelId: string,
  allowedOrigins: readonly string[],
): Promise<WebChatChannelSummary> {
  const result = await apiClient.PUT(
    '/api/v1/tenants/{tenantId}/webchat/channels/{webChatChannelId}/origins',
    {
      params: { path: { tenantId, webChatChannelId } },
      body: { allowedOrigins: [...allowedOrigins] },
    },
  )
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}

/** Rotaciona o signing secret (invalida tokens antigos) e devolve o novo secret uma única vez. */
export async function rotateWebChatSecret(
  tenantId: string,
  webChatChannelId: string,
): Promise<WebChatSecretRotated> {
  const result = await apiClient.POST(
    '/api/v1/tenants/{tenantId}/webchat/channels/{webChatChannelId}/rotate-secret',
    {
      params: { path: { tenantId, webChatChannelId } },
    },
  )
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}

/** Habilita ou desabilita o widget. */
export async function setWebChatStatus(
  tenantId: string,
  webChatChannelId: string,
  enabled: boolean,
): Promise<WebChatChannelSummary> {
  const result = await apiClient.POST(
    '/api/v1/tenants/{tenantId}/webchat/channels/{webChatChannelId}/status',
    {
      params: { path: { tenantId, webChatChannelId } },
      body: { enabled },
    },
  )
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}
